// deployment-g3-recheck.ts
//
// Test 40-41 G3 recheck-before-side-effect, split out of deployment-adapter.ts
// to keep that file ≤200 lines — re-exported so callers only import ONE
// module for "deployment adapter".
import { invalidateGateOnShaChange, readGateFile } from "./gate-registry.js";
import { g3ArtifactHash } from "./release-manifest-hash.js";

export interface RecheckBeforeFirstSideEffectInput {
  controlRoot: string;
  requestId: string;
  currentCandidateSha: string;
  currentReleaseManifestHash: string;
  currentTarget: string;
  approvedTarget: string;
  /** E1: seam bơm được để test hết-hạn không phụ thuộc đồng hồ thật. */
  now?: () => Date;
}

export interface RecheckResult {
  ok: boolean;
  reason: string;
}

/**
 * Test 40-41: called IMMEDIATELY before the first side effect (never reuses
 * a decision cached earlier in the flow). Reads gates.json fresh, confirms
 * g3 is "approved" for THIS request id, and that candidate/target still
 * match what was approved — a SHA change after approval invalidates it via
 * gate-registry.ts's own invalidateGateOnShaChange (does not duplicate that
 * logic here, just calls it then re-reads).
 */
export async function recheckG3ImmediatelyBeforeFirstSideEffect(
  input: RecheckBeforeFirstSideEffectInput,
): Promise<RecheckResult> {
  const artifactHash = g3ArtifactHash(input.currentCandidateSha, input.currentReleaseManifestHash);
  await invalidateGateOnShaChange(input.controlRoot, "g3", artifactHash);
  const gates = await readGateFile(input.controlRoot);
  const g3 = gates.g3;

  if (g3.status !== "approved" && g3.status !== "waived") {
    return {
      ok: false,
      reason: `G3 không ở trạng thái approved/waived (hiện: ${g3.status}) ⇒ chặn deploy.`,
    };
  }
  if (g3.request_id !== input.requestId) {
    return { ok: false, reason: "G3 approval thuộc request khác request hiện tại ⇒ chặn." };
  }
  if (g3.artifact_hash !== artifactHash) {
    return {
      ok: false,
      reason:
        "Candidate SHA / release manifest hash đã đổi kể từ lúc approve G3 ⇒ approval không " +
        "còn hợp lệ, phải request lại (test 41).",
    };
  }
  if (input.currentTarget !== input.approvedTarget) {
    return { ok: false, reason: "Target deploy hiện tại khác target đã duyệt ⇒ chặn." };
  }

  // E1 (plan 20260915-1650 phase-04): phạm vi quyền do NGƯỜI duyệt khai trên
  // chính bản ghi gate. Kiểm SAU phép so target ở trên vì phép so đó đo một
  // thứ khác: "target lúc chạy có đúng target lúc duyệt không". Đo verbatim
  // 2026-09-16: hai vế của nó đều lấy từ CÙNG `config.release.target_branch`
  // (supervisor-release-flow-resume.ts) nên nó chưa từng sai — `targets[]`
  // mới là thứ thật sự giới hạn quyền, và nó là danh sách do người khai.
  //
  // CẢ HAI optional ⇒ gate duyệt kiểu cũ giữ nguyên hành vi (không khai =
  // không siết). Siết một quyền mà người chưa kịp khai là một outage, không
  // phải một cải thiện an toàn.
  if (g3.targets !== undefined && !g3.targets.includes(input.currentTarget)) {
    return {
      ok: false,
      reason:
        `Target "${input.currentTarget}" KHÔNG nằm trong danh sách được duyệt của G3 ` +
        `(${g3.targets.join(", ")}) ⇒ chặn deploy. Muốn thêm target thì request G3 mới.`,
    };
  }

  if (g3.expires_at !== undefined) {
    const expiresAtMs = Date.parse(g3.expires_at);
    if (Number.isNaN(expiresAtMs)) {
      // Fail-CLOSED: quyền có khai hạn nhưng không đọc được hạn ⇒ không suy
      // ra "còn hạn". Trạng thái thẩm quyền KHÔNG xác định thì chặn.
      return {
        ok: false,
        reason: `G3 khai expires_at không đọc được ("${g3.expires_at}") ⇒ chặn deploy (fail-closed).`,
      };
    }
    const nowMs = (input.now ?? (() => new Date()))().getTime();
    if (nowMs >= expiresAtMs) {
      return {
        ok: false,
        reason:
          `Quyền deploy G3 đã HẾT HẠN lúc ${g3.expires_at} ⇒ chặn. ` +
          "KHÔNG có đường tự gia hạn — phải request G3 mới và có người duyệt lại.",
      };
    }
  }

  return { ok: true, reason: "G3 approval khớp candidate/target hiện tại." };
}
